/**
 * Simple WebSocket client wrapper for Stream Overlay Widgets.
 * Handles authentication and message routing.
 */
class WidgetClient {
  constructor(url, token) {
    this.url = url;
    this.token = token;
    this.ws = null;
    this.connected = false;
    this.reconnectAttempt = 0;
    this.maxReconnectDelay = 30000;
    this.baseReconnectDelay = 2000;
    this.shouldReconnect = false;

    // Callbacks
    this.onStatusChange = (status, text, code) => { };
    this.onMessage = (data) => { };
    this.onError = (err) => { };
    this.onLog = (message, type) => { }; // type: info, success, warning, error
    this.retryTimer = null;
  }

  connect() {
    this.shouldReconnect = true;
    this.reconnectAttempt = 0;
    this._establishConnection();
  }

  _establishConnection() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }

    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.onopen = null;
      this.ws.close();
    }

    this.onStatusChange('connecting', 'Connecting...');
    this.onLog('Attempting to connect to ' + this.url, 'info');

    try {
      this.ws = new WebSocket(this.url);
    } catch (e) {
      this.onLog('WebSocket initialization error: ' + e.message, 'error');
      this._retry();
      return;
    }

    // Connection timeout - if it doesn't open or close within 10s, something is stuck
    const connectionTimeout = setTimeout(() => {
      if (this.ws && (this.ws.readyState === WebSocket.CONNECTING)) {
        this.onLog('Connection timed out', 'error');
        this.ws.close();
        this._retry();
      }
    }, 10000);

    this.ws.onopen = () => {
      clearTimeout(connectionTimeout);
      this.onLog('WebSocket connection established. Authenticating...', 'info');
      // Phase 1: Authentication
      this.ws.send(JSON.stringify({ token_id: this.token }));
    };

    this.ws.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        console.error('Failed to parse WS message', e);
        this.onLog('Received invalid JSON message', 'error');
        return;
      }

      // Handle Authentication Success
      if (data.status === 'authenticated') {
        this.connected = true;
        this.reconnectAttempt = 0;
        this.onStatusChange('connected', `Subscribed â€” ${data.room}`);
        this.onLog('Successfully authenticated and subscribed to room: ' + data.room, 'success');
        return;
      }

      // Handle Errors
      if (data.error) {
        this.onStatusChange('disconnected', `Error: ${data.message || data.error}`);
        this.onLog('Server error: ' + (data.message || data.error), 'error');
        return;
      }

      // Handle Server Maintenance / Restart
      if (data.type === 'server_restarting') {
        this.onLog(`ðŸ“£ ${data.message || 'Server is restarting'}`, 'warning');
        this.onStatusChange('restarting', 'Server updating...');
        
        // Allow the server to shut down before we try to reconnect
        const delay = data.reconnect_delay || 5000;
        this.onLog(`Will attempt reconnection in ${(delay / 1000).toFixed(1)}s`, 'info');
        
        // Temporarily disable auto-reconnect on next close so we can control it here
        const originalShouldReconnect = this.shouldReconnect;
        this.shouldReconnect = false;
        this.ws.close(1001, 'Server restarting');
        this.shouldReconnect = originalShouldReconnect;

        // Schedule the manual retry
        if (this.retryTimer) clearTimeout(this.retryTimer);
        this.retryTimer = setTimeout(() => {
          this.retryTimer = null;
          if (this.shouldReconnect) {
            this._establishConnection();
          }
        }, delay);
        return;
      }

      // Handle Control Messages
      this.onMessage(data);
    };

    this.ws.onclose = (event) => {
      clearTimeout(connectionTimeout);
      const wasConnected = this.connected;
      this.connected = false;

      let reason = 'Connection failed or closed';
      if (event.code === 1000) reason = 'Normal closure';
      else if (event.code === 1006) reason = 'Abnormal closure (server down or network issue)';
      else if (event.code === 1001) reason = 'Server is going away';

      this.onStatusChange('disconnected', `Disconnected (${event.code})`, event.code);
      this.onLog(`${reason} (code: ${event.code})`, wasConnected ? 'warning' : 'error');

      if (this.shouldReconnect) {
        this._retry();
      }
    };

    this.ws.onerror = (err) => {
      clearTimeout(connectionTimeout);
      this.onError(err);
      // We don't log here because onclose will trigger and provide the code/reason
    };
  }

  _retry() {
    if (!this.shouldReconnect) return;
    if (this.retryTimer) clearTimeout(this.retryTimer);

    this.reconnectAttempt++;
    const delay = Math.min(this.baseReconnectDelay * Math.pow(1.5, this.reconnectAttempt - 1), this.maxReconnectDelay);

    this.onLog(`Retrying connection (attempt ${this.reconnectAttempt}) in ${(delay / 1000).toFixed(1)}s...`, 'info');

    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.shouldReconnect) {
        this._establishConnection();
      }
    }, delay);
  }

  disconnect() {
    this.shouldReconnect = false;
    this.connected = false;
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.ws) {
      this.ws.close(1000, 'User disconnected');
    }
    this.onLog('Manually disconnected', 'info');
    this.onStatusChange('disconnected', 'Disconnected');
  }
}

// Export for usage in HTML
window.WidgetClient = WidgetClient;
